import React from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag,
  MapPin,
  Truck,
  Phone,
  ExternalLink,
  Pizza as PizzaIcon,
} from "lucide-react";
import { RESTAURANT } from "@/data/catalog";
import { DeliveryDialog } from "@/components/DeliveryDialog";
import { useCart } from "@/lib/cartStore";
import { useRestaurant } from "@/lib/brandingStore";

/**
 * Bloc "Comment commander ?" — utilisé sur l'Accueil (3 choix) et sur
 * /commander (2 choix : À emporter / Livraison).
 *
 * Variant:
 *  - "home"      → 3 cartes (À emporter / Sur place / Livraison)
 *  - "commander" → 2 cartes (À emporter / Livraison)
 *
 * Palette apaisée — un seul orange fort (CTA "Composer ma pizza"), tout le
 * reste passe sur ivoire / cuivré discret.
 */
export const HowToOrderBlock = ({ variant = "home" }) => {
  const isCommander = variant === "commander";
  const { openDrawer, count } = useCart();
  const r = useRestaurant();

  const mapsUrl =
    r.maps_url ||
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${r.address_street} ${r.address_zip} ${r.address_city}`
    )}`;
  const phoneTel = r.phone_tel || RESTAURANT.phoneTel;
  const restoName = r.name || RESTAURANT.name;

  const secondaryBtn =
    "font-street inline-flex items-center gap-1.5 rounded-md border border-[#F5EFE6]/18 bg-[#F5EFE6]/[0.03] px-3 py-1.5 text-[11px] tracking-wider text-[#F5EFE6]/85 hover:border-[#F5EFE6]/35 hover:bg-[#F5EFE6]/[0.06] transition-colors";

  return (
    <section
      data-testid={`how-to-order-${variant}`}
      className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-10"
    >
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#C9A875] font-street">
            {isCommander ? "Mode de commande" : "En 3 étapes"}
          </div>
          <h2 className="font-street mt-2 text-2xl sm:text-3xl tracking-wide leading-none">
            {isCommander ? "À emporter ou livraison ?" : "Comment commander ?"}
          </h2>
        </div>
      </div>

      <div
        className={`grid gap-3 sm:gap-4 ${
          isCommander ? "sm:grid-cols-2" : "sm:grid-cols-3"
        }`}
      >
        {/* ── À EMPORTER (carte "mise en avant" sobre, sans fond orange) ── */}
        <article
          data-testid={`how-to-order-${variant}-takeaway`}
          className="rounded-2xl border border-[#C9A875]/30 bg-[#121212] p-4 sm:p-5 flex flex-col"
        >
          <div className="flex items-center gap-2.5 mb-2">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#C9A875]/10 border border-[#C9A875]/30 text-[#C9A875] shrink-0">
              <ShoppingBag size={17} strokeWidth={2.4} />
            </span>
            <div className="font-street text-base sm:text-lg tracking-wider uppercase leading-none">
              À emporter
            </div>
          </div>
          <p className="text-[12.5px] sm:text-[13px] text-[#D4D4D8] leading-snug flex-1">
            {isCommander
              ? "Configurez votre pizza, ajoutez-la au récapitulatif, puis appelez " +
                restoName +
                " pour confirmer."
              : "Préparez votre commande, consultez la carte ou configurez votre pizza, puis appelez " +
                restoName +
                " pour confirmer."}
          </p>

          {/* Actions */}
          <div className="mt-3 flex flex-wrap gap-2">
            {isCommander ? (
              <>
                {count > 0 && (
                  <button
                    type="button"
                    onClick={openDrawer}
                    data-testid={`how-to-order-${variant}-see-recap`}
                    className={secondaryBtn}
                  >
                    Voir mon récap
                  </button>
                )}
                <a
                  href={phoneTel}
                  data-testid={`how-to-order-${variant}-call`}
                  className={secondaryBtn}
                >
                  <Phone size={11} strokeWidth={2.6} className="text-[#C9A875]" />
                  Appeler
                </a>
              </>
            ) : (
              <>
                <Link
                  to="/commander"
                  data-testid={`how-to-order-${variant}-configure`}
                  className="font-street inline-flex items-center gap-1.5 rounded-md bg-[#FF9500] px-3 py-1.5 text-[11px] tracking-wider text-black shadow-[0_3px_0_#7a4900] hover:-translate-y-0.5 hover:bg-[#FFAA1F] transition-all"
                >
                  <PizzaIcon size={11} strokeWidth={2.6} />
                  Composer ma pizza
                </Link>
                <Link
                  to="/menu"
                  data-testid={`how-to-order-${variant}-menu`}
                  className={secondaryBtn}
                >
                  Voir la carte
                </Link>
                <a
                  href={phoneTel}
                  data-testid={`how-to-order-${variant}-call`}
                  className={secondaryBtn}
                >
                  <Phone size={11} strokeWidth={2.6} className="text-[#C9A875]" />
                  Appeler
                </a>
              </>
            )}
          </div>
        </article>

        {/* ── SUR PLACE (home only) ───────────────────────────────── */}
        {!isCommander && (
          <article
            data-testid={`how-to-order-${variant}-onsite`}
            className="rounded-2xl border border-[#262626] bg-[#121212] p-4 sm:p-5 flex flex-col"
          >
            <div className="flex items-center gap-2.5 mb-2">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#C9A875]/10 border border-[#C9A875]/25 text-[#C9A875] shrink-0">
                <MapPin size={17} />
              </span>
              <div className="font-street text-base sm:text-lg tracking-wider uppercase leading-none">
                Sur place
              </div>
            </div>
            <p className="text-[12.5px] sm:text-[13px] text-[#D4D4D8] leading-snug flex-1">
              Venez directement au restaurant.
              <br />
              <span className="text-white">{r.address_street}</span>
              <br />
              <span className="text-white">
                {r.address_zip} {r.address_city}
              </span>
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`how-to-order-${variant}-maps`}
                className={secondaryBtn}
              >
                Itinéraire <ExternalLink size={11} />
              </a>
              <Link
                to="/contact"
                data-testid={`how-to-order-${variant}-hours`}
                className="font-street inline-flex items-center gap-1.5 rounded-md border border-[#262626] px-3 py-1.5 text-[11px] tracking-wider text-white/65 hover:text-white"
              >
                Horaires
              </Link>
            </div>
          </article>
        )}

        {/* ── LIVRAISON ─────────────────────────────────────────── */}
        <article
          data-testid={`how-to-order-${variant}-delivery`}
          className="rounded-2xl border border-[#262626] bg-[#121212] p-4 sm:p-5 flex flex-col"
        >
          <div className="flex items-center gap-2.5 mb-2">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#C9A875]/10 border border-[#C9A875]/25 text-[#C9A875] shrink-0">
              <Truck size={17} />
            </span>
            <div className="font-street text-base sm:text-lg tracking-wider uppercase leading-none">
              Livraison
            </div>
          </div>
          <p className="text-[12.5px] sm:text-[13px] text-[#D4D4D8] leading-snug flex-1">
            {isCommander
              ? "La livraison sera bientôt disponible via Uber Eats et Deliveroo."
              : "La livraison sera prochainement disponible via nos plateformes partenaires."}
          </p>
          <div className="mt-3">
            <DeliveryDialog
              trigger={
                <button
                  type="button"
                  data-testid={`how-to-order-${variant}-delivery-trigger`}
                  className={secondaryBtn}
                >
                  <Truck size={11} strokeWidth={2.6} className="text-[#C9A875]" />
                  Bientôt disponible
                </button>
              }
            />
          </div>
        </article>
      </div>
    </section>
  );
};

export default HowToOrderBlock;
